/**
 * Date Formatting Utility
 * Handles relative time formatting for timestamps
 */

/**
 * Formats a date as relative time (e.g., "2m ago", "5h ago", "3d ago")
 * 
 * @param date - The date to format
 * @returns Formatted relative time string
 * 
 * @example
 * formatRelativeTime(new Date(Date.now() - 120000)) // "2m ago"
 * formatRelativeTime(new Date(Date.now() - 3600000)) // "1h ago"
 */
export function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  // Less than 1 minute
  if (diffSeconds < 60) {
    return 'Just now';
  }

  // Less than 60 minutes
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }

  // Less than 24 hours
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  // 24 hours or more
  return `${diffDays}d ago`;
}

/**
 * Formats a date as a full timestamp string
 * 
 * @param date - The date to format
 * @returns Formatted timestamp string (e.g., "Mar 11, 2026 10:30 AM")
 */
export function formatFullTimestamp(date: Date): string {
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}
